import type { PaymentOption } from '../api/types';

/** Minimal EIP-1193 provider surface we rely on. */
export interface Eip1193Provider {
  request(args: { method: string; params?: unknown[] | Record<string, unknown> }): Promise<unknown>;
  on?(event: string, listener: (...args: unknown[]) => void): void;
  removeListener?(event: string, listener: (...args: unknown[]) => void): void;
  isMetaMask?: boolean;
  isCoinbaseWallet?: boolean;
  providers?: Eip1193Provider[];
}

/** EIP-6963 announced provider detail. */
export interface Eip6963ProviderDetail {
  info: { uuid: string; name: string; icon: string; rdns: string };
  provider: Eip1193Provider;
}

export type WalletId =
  | `injected:${string}` // EIP-6963 rdns or "injected:window.ethereum"
  | 'walletconnect'
  | 'coinbase'
  | 'phantom'
  | `solana-standard:${string}`
  | 'tronlink';

export interface WalletDescriptor {
  id: WalletId;
  name: string;
  /** data: URL or https icon; undefined -> initials badge. */
  icon?: string;
  /** Which payment options this wallet can pay. */
  supports(option: PaymentOption): boolean;
  /** Installed/available right now (false => show install / deep-link). */
  available: boolean;
  installUrl?: string;
}

export type PayProgress = 'connecting' | 'switching' | 'confirm' | 'sent';

export interface PayContext {
  option: PaymentOption;
  onProgress(stage: PayProgress): void;
}

export interface PayResult {
  txHash: string;
}

export class WalletError extends Error {
  constructor(
    public code: 'rejected' | 'not_installed' | 'unsupported' | 'insufficient' | 'misconfigured' | 'failed',
    message: string,
  ) {
    super(message);
  }
}

/** Normalise wallet-thrown errors into WalletError. */
export function normalizeWalletError(e: unknown): WalletError {
  if (e instanceof WalletError) return e;
  const anyE = e as { code?: number | string; message?: string; name?: string } | undefined;
  const msg = anyE?.message ?? String(e);
  const code = anyE?.code;
  if (code === 4001 || code === 'ACTION_REJECTED' || /rejected|denied|cancel/i.test(msg)) {
    return new WalletError('rejected', msg);
  }
  if (/insufficient/i.test(msg)) return new WalletError('insufficient', msg);
  return new WalletError('failed', msg.length > 200 ? msg.slice(0, 200) + '…' : msg);
}
